import { useEffect, useId, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { ChevronDown } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';
import { useIsMobile } from '@worldcoo/ui/hooks/use-mobile';
import type { CategoryKey } from '@worldcoo/shared/core';
import { Image } from '@/routes/-components/image';
import { CATEGORY_ICONS } from './category-icons';
import { NgoPagination } from './ngo-pagination';
import type { PartnerNgo } from './use-partner-ngos';

/**
 * How many partners a row shows, per breakpoint. Both come straight off the hand-off: the desktop
 * component opens to five rows of seven and paginates beyond that, and the mobile one opens to three
 * rows of two and grows by another three on "Ver más".
 */
const DESKTOP_PAGE = 35;
const MOBILE_PAGE = 6;
/** Closed, desktop still shows its first line of logos; mobile shows none at all. */
const DESKTOP_CLOSED = 7;

/** One logo plate. A button, because the whole point of the grid is to open the NGO's projects. */
function NgoLogo(props: { ngo: PartnerNgo; onOpen: (ngo: PartnerNgo) => void }) {
	const { t } = useLingui();
	// lingui disallows a property access inside t`` — bind it to a plain variable first.
	const name = props.ngo.name;
	return (
		<button
			type="button"
			onClick={() => props.onOpen(props.ngo)}
			aria-label={t`See the projects of ${name}`}
			className={cn(
				'flex h-16 items-center justify-center rounded-2xl bg-[#f8f8f8] px-3 lg:h-[79px] lg:rounded-[16.7px] lg:px-4',
				'focus-visible:ring-ink cursor-pointer transition-opacity hover:opacity-70 focus-visible:ring-2 focus-visible:outline-none',
			)}
		>
			{props.ngo.logo ? (
				/* Partner artwork of unknown transparency, so `grayscale` rather than a forced black
				   silhouette — same treatment, and same reasoning, as the contact page's logo strip. */
				<Image
					src={props.ngo.logo.location}
					alt={props.ngo.name}
					height={48}
					aspectRatio={2}
					objectFit="contain"
					className="max-h-10 w-full object-contain! grayscale lg:max-h-12"
				/>
			) : (
				<span className="text-ink/60 line-clamp-2 text-center text-sm font-medium">{props.ngo.name}</span>
			)}
		</button>
	);
}

/**
 * One category card in the directory.
 *
 * The two breakpoints are genuinely different components in the hand-off, not one reflowed layout, so
 * the differences are real rather than cosmetic:
 *
 * - DESKTOP keeps one line of seven logos visible while closed, and "Ver más" opens it to five lines
 *   with numbered pagination underneath.
 * - MOBILE is a plain accordion: closed shows the title bar only, and opening reveals a two-column
 *   grid that grows six at a time from a "Ver más" at the FOOT of the card, not in its header.
 *
 * Hence `useIsMobile` rather than pure CSS: how many partners to render is a data decision, and no
 * media query can slice an array. It resolves to desktop on the server and during hydration, which is
 * the right default — the desktop branch renders logos, so a crawler sees them either way.
 */
export function NgoCategoryRow(props: {
	category: CategoryKey;
	label: string;
	ngos: PartnerNgo[];
	onOpen: (ngo: PartnerNgo) => void;
	/** Start open — set when a category filter has left this row alone on the page. */
	openByDefault?: boolean;
}) {
	const { t } = useLingui();
	const isMobile = useIsMobile(1024);
	const [open, setOpen] = useState(props.openByDefault ?? false);
	const [page, setPage] = useState(0);
	const [shown, setShown] = useState(MOBILE_PAGE);
	const gridId = useId();
	const Icon = CATEGORY_ICONS[props.category];

	// Filtering can shrink a category under the page the visitor had reached, which would otherwise
	// leave them on an empty page with no way back.
	useEffect(() => {
		setPage(0);
		setShown(MOBILE_PAGE);
	}, [props.ngos.length]);

	// Follows the filter rather than only seeding the initial state: picking a category opens its row,
	// clearing the filter closes it again. A manual toggle in between still wins until it next changes.
	const openByDefault = props.openByDefault ?? false;
	useEffect(() => setOpen(openByDefault), [openByDefault]);

	const pageCount = Math.ceil(props.ngos.length / DESKTOP_PAGE);
	const visible = !open
		? isMobile
			? []
			: props.ngos.slice(0, DESKTOP_CLOSED)
		: isMobile
			? props.ngos.slice(0, shown)
			: props.ngos.slice(page * DESKTOP_PAGE, (page + 1) * DESKTOP_PAGE);

	// Desktop closed already shows a full line, so there is only something to open when there is more
	// than a line. Mobile closed shows nothing, so its toggle is always meaningful.
	const canToggle = isMobile || props.ngos.length > DESKTOP_CLOSED;
	const canShowMore = isMobile && open && shown < props.ngos.length;

	// The whole bar is the control, not just the chevron: a 16px glyph is a poor tap target on a phone,
	// where the row is a plain accordion and tapping the category name is the obvious way to open it.
	const header = (
		<>
			<span className="flex min-w-0 items-center gap-2">
				<span className="bg-ink grid size-8 shrink-0 place-items-center rounded-lg text-white">
					<Icon className="size-4" />
				</span>
				<span className="text-base leading-[1.3] font-medium lg:text-xl">{props.label}</span>
			</span>

			{canToggle && (
				<span className="text-ink flex shrink-0 items-center gap-2 text-[15px] font-medium tracking-[-0.02em]">
					{/* The mobile header carries the chevron alone — the label would crowd a long category
					    name like "Educación e Inserción laboral" at 343px. */}
					<span className="sr-only lg:not-sr-only">{open ? t`See less` : t`See more`}</span>
					<ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} />
				</span>
			)}
		</>
	);

	return (
		<section className="rounded-2xl bg-white p-4 lg:rounded-3xl lg:p-8">
			<h3>
				{canToggle ? (
					<button
						type="button"
						onClick={() => setOpen((isOpen) => !isOpen)}
						aria-expanded={open}
						aria-controls={gridId}
						className="flex w-full cursor-pointer items-center justify-between gap-4 text-left hover:opacity-70"
					>
						{header}
					</button>
				) : (
					/* Nothing to open — a desktop row of seven or fewer is already showing everything, so the
					   bar is not a control and must not look or behave like one. */
					<span className="flex w-full items-center justify-between gap-4">{header}</span>
				)}
			</h3>

			{visible.length > 0 && (
				<div id={gridId} className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:mt-6 lg:grid-cols-7 lg:gap-6">
					{visible.map((ngo) => (
						<NgoLogo key={ngo.id} ngo={ngo} onOpen={props.onOpen} />
					))}
				</div>
			)}

			{canShowMore && (
				<button
					type="button"
					onClick={() => setShown((count) => count + MOBILE_PAGE)}
					className="text-ink mt-6 flex cursor-pointer items-center gap-2 text-base font-medium tracking-[-0.02em] hover:opacity-70"
				>
					{t`See more`}
					<ChevronDown className="size-4" />
				</button>
			)}

			{open && !isMobile && <NgoPagination page={page} pageCount={pageCount} onChange={setPage} />}
		</section>
	);
}
